import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Badge, Checkbox, Tag, Tooltip, Avatar, Space, Dropdown, Typography, message, Popconfirm, Table } from 'antd';
import { ChevronDown, ChevronRight, Clock, Filter, MenuIcon, MoreVertical, SortAsc, SortDesc, Trash2, Edit } from 'lucide-react';
import KanbanTaskStore, { KanbanTask } from '../../stores/KanbanTaskStores';
import { getPriorityColor, getPriorityIcon, getPriorityText, formatDate, getTimeRemaining } from './useTaskHelpers';
import dayjs from 'dayjs';
import { useStore } from '../../stores/root-store-context';
import { ka } from 'date-fns/locale';
import type { ColumnsType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { getStatusColor } from '../../helpers/statusColors';

const { Text } = Typography;

interface ListViewProps {
  tasks: KanbanTask[];
  statuses: any[];
  users: any[];
  onTaskClick: (task: KanbanTask) => void;
  t: (key: string) => string;
  showDeletedTasks: boolean;
  setShowDeletedTasks: (value: boolean) => void;
}

type TableRow = KanbanTask & {
  key: string;
  statusName: string;
};

const ListView: React.FC<ListViewProps> = ({ tasks, statuses, users, onTaskClick, t, showDeletedTasks, setShowDeletedTasks }) => {
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);

  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>(
    () => statuses.map(s => s.id)
  );

  const [hideCompletedTasks, setHideCompletedTasks] = useState<boolean>(false);
  const [showOverdueTasks, setShowOverdueTasks] = useState<boolean>(false);
  const { applicationStore, kanbanTaskStore } = useStore();

  const tableWrapRef = useRef<HTMLDivElement | null>(null);
  const [tableBodyHeight, setTableBodyHeight] = useState<number>(400);

  const dataSource = useMemo(() => {
    let data = [...tasks];

    if (selectedStatusIds.length > 0) {
      data = data.filter(t => selectedStatusIds.includes(t.statusId));
    }

    if (hideCompletedTasks) {
      const completedStatusIds = statuses
        .filter(s => /closed|completed|done/i.test(s.name))
        .map(s => s.id);

      data = data.filter(t => !completedStatusIds.includes(t.statusId));
    }

    if (showOverdueTasks) {
      data = data.filter(
        t => t.dueDate && dayjs(t.dueDate).isBefore(dayjs(), 'day')
      );
    }

    return data.map(task => ({
      key: task.id,
      ...task,
      statusName: statuses.find(s => s.id === task.statusId)?.name ?? ''
    }));
  }, [tasks, statuses, hideCompletedTasks, showOverdueTasks, selectedStatusIds]);

  const statusColorMap = useMemo(() => {
    return statuses.reduce<Record<string, string>>((acc, status, index) => {
      acc[status.id] = getStatusColor(status.id, index);
      return acc;
    }, {});
  }, [statuses]);

  useLayoutEffect(() => {
    const el = tableWrapRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      const headerHeight = 56;
      setTableBodyHeight(Math.max(200, Math.floor(h - headerHeight)));
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);


  const deleteSelectedTasks = async () => {
    try {
      if (showDeletedTasks)
        await kanbanTaskStore.bulkPermanentDelete(selectedTaskIds);
      else
        await kanbanTaskStore.bulkSoftDelete(selectedTaskIds);

      message.success(
        showDeletedTasks ? t('SelectedTasksDeletedPermanent') : t('SelectedTasksDeleted')
      );

      setSelectedTaskIds([]);

    } catch (err) {
      console.error(err);
      message.error(t('FailedToDeleteSelected'));
    }
  };

  const restoreSelectedTasks = async () => {
    try {
      await kanbanTaskStore.bulkRestore(selectedTaskIds);

      message.success(t('SelectedTasksRestored'));

      setSelectedTaskIds([]);

    } catch (error) {
      console.error(error);
      message.error(t('FailedToRestoreSelected'));
    }
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      sorter: (a: any, b: any) => a.title.localeCompare(b.title),
      render: (_: any, task: any) => (
        <a onClick={() => onTaskClick(task)}>{task.title}</a>
      )
    },
    {
      title: t('PublicId'),
      dataIndex: 'publicId',
      sorter: (a: KanbanTask, b: KanbanTask) =>
        (a.publicId ?? '').localeCompare(b.publicId ?? ''),
      render: (v: string | undefined) =>
        v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: t('Priority'),
      dataIndex: 'priority',
      sorter: (a: any, b: any) => a.priority - b.priority,
      render: (p: any) => (
        <Tag color={getPriorityColor(p)}>
          {getPriorityIcon(p)} {getPriorityText(p, t)}
        </Tag>
      )
    },
    {
      title: t('Assignee'),
      dataIndex: 'assigneeId',
      render: (_: any, task: any) => {
        if (!task.assigneeId) {
          return <Text type="secondary">{t('Unassigned')}</Text>;
        }

        const user = users.find(u => String(u.id) === String(task.assigneeId));
        return (
          <Space>
            <Avatar
              size="small"
              src={user?.photoId
                ? `${applicationStore.siteConfig.baseCloudinary}/image/upload/w_100/${user.photoId}`
                : undefined}
            >
              {user?.firstName?.[0]}
            </Avatar>
            {user?.firstName} {user?.lastName}
          </Space>
        );
      }
    },
    {
      title: t('StartDate'),
      dataIndex: 'startDate',
      sorter: (a: any, b: any) =>
        new Date(a.startDate ?? 0).getTime() -
        new Date(b.startDate ?? 0).getTime(),
      render: (d: any) => formatDate(d)
    },
    {
      title: t('DueDate'),
      dataIndex: 'dueDate',
      sorter: (a: any, b: any) =>
        new Date(a.dueDate ?? 0).getTime() -
        new Date(b.dueDate ?? 0).getTime(),
      render: (_: any, task: any) => {
        const remaining = task.dueDate
          ? getTimeRemaining(task.dueDate, t)
          : null;

        return (
          <Space>
            {formatDate(task.dueDate)}
            {remaining && (
              <Tag color={remaining.color}>
                <Clock size={12} /> {remaining.text}
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: t('ModifiedDate'),
      dataIndex: 'modifiedDate',
      defaultSortOrder: 'descend',
      sorter: (a: KanbanTask, b: KanbanTask) =>
        dayjs(a.modifiedDate).valueOf() - dayjs(b.modifiedDate).valueOf(),
      render: (d: KanbanTask['modifiedDate']) =>
        d ? formatDate(d.toString()) : <Text type="secondary">—</Text>
    },
    {
      title: t('Status'),
      dataIndex: 'statusName',
      sorter: (a: any, b: any) => a.statusName.localeCompare(b.statusName),
      render: (_: any, row) => (
        <Tag color={statusColorMap[row.statusId]}>
          {row.statusName}
        </Tag>
      )

    }
  ];

  const statusFilterMenu = (
    <div
      style={{
        padding: 12,
        maxHeight: 300,
        overflowY: 'auto',
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        border: '1px solid #f0f0f0',
        minWidth: 220
      }}
    >
      <Space direction="vertical">
        <Checkbox
          checked={selectedStatusIds.length === statuses.length}
          indeterminate={
            selectedStatusIds.length > 0 &&
            selectedStatusIds.length < statuses.length
          }
          onChange={(e) =>
            setSelectedStatusIds(
              e.target.checked ? statuses.map(s => s.id) : []
            )
          }
        >
          {t('SelectAll')}
        </Checkbox>

        {statuses.map(status => (
          <Checkbox
            key={status.id}
            checked={selectedStatusIds.includes(status.id)}
            onChange={(e) => {
              setSelectedStatusIds(prev =>
                e.target.checked
                  ? [...prev, status.id]
                  : prev.filter(id => id !== status.id)
              );
            }}
          >
            <Tag color={statusColorMap[status.id]}>
              {status.name}
            </Tag>
          </Checkbox>
        ))}
      </Space>
    </div>
  );

  return (
    <div
      className="list-view"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '80vh'
      }}
    >
      <div className="list-controls">
        <Space>
          <Button
            icon={<Filter size={16} />}
            onClick={() => setHideCompletedTasks(!hideCompletedTasks)}
            type={hideCompletedTasks ? 'primary' : 'default'}
            className="hover-effect"
          >
            {hideCompletedTasks ? t('ShowingActive') : t('ShowAll')}
          </Button>
          <Button
            icon={<Clock size={16} />}
            onClick={() => setShowOverdueTasks(!showOverdueTasks)}
            type={showOverdueTasks ? 'primary' : 'default'}
            className="hover-effect"
          >
            {showOverdueTasks ? t('ShowingOverdue') : t('ShowAllDates')}
          </Button>
          <Button
            type={showDeletedTasks ? 'primary' : 'default'}
            onClick={() => setShowDeletedTasks(!showDeletedTasks)}
            className="hover-effect"
          >
            {showDeletedTasks ? t('ShowAllTasks') : t('ShowDeletedTasks')}
          </Button>
          <Dropdown
            overlay={statusFilterMenu}
            trigger={['click']}
            className="hover-effect"
          >
            <Button icon={<Filter size={16} />}>
              {t('Statuses')}
              {selectedStatusIds.length !== statuses.length && (
                <Badge
                  count={selectedStatusIds.length}
                  style={{ marginLeft: 8 }}
                />
              )}
            </Button>
          </Dropdown>

          {showDeletedTasks && selectedTaskIds.length > 0 && (
            <Badge count={selectedTaskIds.length} offset={[5, 0]} style={{ marginRight: 10 }}>
              <Popconfirm
                title={t('ConfirmRestore')}
                description={t('AreYouSureRestore')}
                okText={t('Restore')}
                cancelText={t('Cancel')}
                onConfirm={restoreSelectedTasks}
              >
                <Button type="primary" className="hover-effect">
                  {t('RestoreSelected')}
                </Button>
              </Popconfirm>
            </Badge>
          )}
          {selectedTaskIds.length > 0 && (
            <Badge count={selectedTaskIds.length} offset={[5, 0]}>
              <Popconfirm
                title={t('ConfirmDeleteTask')}
                description={
                  showDeletedTasks
                    ? t('AreYouSureDeleteTaskPermanent')
                    : t('AreYouSureDeleteTask')
                }
                okText={t('Delete')}
                okType="danger"
                cancelText={t('Cancel')}
                onConfirm={deleteSelectedTasks}
              >
                <Button danger icon={<Trash2 size={16} />} className="hover-effect">
                  {t('DeleteSelected')}
                </Button>
              </Popconfirm>
            </Badge>
          )}
        </Space>
      </div>

      <div
        ref={tableWrapRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden'
        }}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowSelection={{
            selectedRowKeys: selectedTaskIds,
            onChange: (keys) => setSelectedTaskIds(keys as string[])
          }}
          pagination={false}
          scroll={{ y: tableBodyHeight }}
          onRow={(task) => ({
            onDoubleClick: () => onTaskClick(task)
          })}
        />
      </div>
    </div>
  );
};

export default ListView;